import React, { type FC } from "react";

import { RefreshIntervalSelector } from "~/components/refresh-interval-selector";
import type { RefreshInterval } from "~/components/refresh-interval-selector/utils";
import type { Time } from "~/tools/date-time/time";
import { type Impulse, useScope, useScoped } from "~/tools/impulse";
import { useDebounce } from "~/tools/use-debounce";

import type { TimeRange } from "../../../../../../app/state/view/timeline/TimeRange";
import { DropdownFilter, type DropdownFilterItem } from "../../../../../../components/_dropdown-filter";
import { ButtonGroup } from "../../../../../../components/react/buttons/Button";
import { SearchInput } from "../../../../../../components/react/input/SearchInput";
import { TimelineRangeSelectorExtended } from "../../../../../../components/react/timeline/TimelineRangeSelectorExtended";
import { useQueryPodContainerNames } from "../../../../../../queries/podLogs";
import { useRefetchQueriesInNamespace } from "../../../../../../queries/use-refetch-queries-in-namespace";

import { DataCountInformation } from "./_data-count-information";
import { MIN_FILTER_LENGTH, SEVERITY_ITEMS } from "./_element-properties-logs-drawer-filter-constants";
import { LOGS_DRAWER_QUERY_NAMESPACE } from "./_query-namespace";

export interface FilterState {
	search: Impulse<string | undefined>;
	severity: Impulse<DropdownFilterItem>;
	container: Impulse<DropdownFilterItem>;
}

export interface ElementPropertiesLogsDrawerFiltersProps {
	cluster: string;
	podUid: string;
	isFetching: boolean;
	totalDBRowCount: number;
	timestamp?: Time;
	timeRange: Impulse<TimeRange>;
	filterState: FilterState;
}

export const ElementPropertiesLogsDrawerFilters: FC<ElementPropertiesLogsDrawerFiltersProps> = ({
	cluster,
	podUid,
	isFetching,
	totalDBRowCount,
	timestamp,
	timeRange,
	filterState,
}) => {
	const scope = useScope();
	const timeRangeValue = useScoped(timeRange);

	const [refreshInterval, setRefreshInterval] = React.useState<RefreshInterval>("off");
	const [isRefetching, refetchQueries] = useRefetchQueriesInNamespace(LOGS_DRAWER_QUERY_NAMESPACE);
	const { data: containerItems = [] } = useQueryPodContainerNames(
		{
			cluster,
			podUID: podUid,
			timestamp,
			timeRange: timeRangeValue,
		},
		{
			select: (data) => data.map((name) => ({ value: name, label: name })),
		},
	);

	const onSearch = useDebounce((value: string) => {
		if (value.length === 0) {
			filterState.search.setValue(undefined);
		} else if (value.length >= MIN_FILTER_LENGTH) {
			filterState.search.setValue(value);
		}
	}, 500);

	return (
		<div className="flex flex-wrap justify-between gap-4 py-2">
			<DataCountInformation total={totalDBRowCount} />

			<div className="ml-4 flex grow flex-wrap justify-end gap-4">
				<ButtonGroup className="justify-end max-xs:flex-wrap">
					<SearchInput
						className="flex-1"
						isFetching={isFetching}
						onSearch={onSearch}
						placeholder={`Search (min. ${MIN_FILTER_LENGTH} characters)`}
						value={filterState.search.getValue(scope) ?? ""}
					/>
					<DropdownFilter items={SEVERITY_ITEMS} label="Severity" selectedItem={filterState.severity} />
					<DropdownFilter items={containerItems} label="Container" selectedItem={filterState.container} />
				</ButtonGroup>

				<TimelineRangeSelectorExtended contextName="logs" timeRange={timeRange} />

				<RefreshIntervalSelector
					disabled={timeRangeValue.isSpecific()}
					loading={isRefetching}
					onChange={setRefreshInterval}
					onRefresh={refetchQueries}
					value={refreshInterval}
				/>
			</div>
		</div>
	);
};
